import React, { useState, useRef } from 'react';
import { Send, Loader, Paperclip, X } from 'lucide-react';

const V2ChatInput = ({
  onSendMessage,
  isLoading,
  placeholder = "Type your message...",
  disabled = false
}) => {
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();

    if ((!message.trim() && !selectedFile) || isLoading || disabled) return;

    // Send message with optional file
    onSendMessage(message.trim(), selectedFile);

    // Clear input and file
    setMessage('');
    setSelectedFile(null);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleChange = (e) => {
    setMessage(e.target.value);

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  const canSend = message.trim() && !isLoading && !disabled;

  return (
    <div className="p-4">
      <form onSubmit={handleSubmit} className="flex items-end space-x-3">
        {/* Message input */}
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? "Chat unavailable" : placeholder}
            disabled={disabled}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
            rows={1}
            style={{ minHeight: '44px', maxHeight: '120px' }}
          />

          {/* Input hints */}
          <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span>
              {disabled
                ? "Authentication required"
                : "Press Enter to send, Shift+Enter for new line"
              }
            </span>
            {message.length > 0 && (
              <span className={message.length > 1000 ? 'text-red-500' : ''}>
                {message.length}/2000
              </span>
            )}
          </div>
        </div>

        {/* Send button */}
        <button
          type="submit"
          disabled={!canSend}
          className="p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-w-[44px] h-[44px]"
          title={isLoading ? "Sending..." : "Send message"}
        >
          {isLoading ? (
            <Loader className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </form>

      {/* Status indicators - removed text, just showing in chat messages now */}
    </div>
  );
};

export default V2ChatInput;